import type { Lead } from '@/data/leads.service';
import {
  INACTION_THRESHOLDS,
  INACTION_SEVERITY_BASE,
  getSlaMs,
  clamp,
  type InactionSourceCategory,
} from './inactionConfig';
import { INACTION_COPY } from './inactionCopy';

/** Lead-like shape for timing: anchor = last contact or created; due = next_follow_up_at or anchor + SLA */
export interface LeadTimingInput {
  created_at?: string | null;
  last_contacted_at?: string | null;
  last_interaction_at?: string | null;
  last_activity_at?: string | null;
  first_contacted_at?: string | null;
  next_follow_up_at?: string | null;
  temperature?: string | null;
  insertedAt?: string | null;
}

export interface LeadTimingResult {
  /** Anchor for "time since": last outbound contact or created_at */
  anchorAt: Date | null;
  /** Milliseconds since anchor (0 if no anchor) */
  timeSinceAnchorMs: number;
  /** Due date: next_follow_up_at or anchor + SLA(temperature) */
  dueAt: Date | null;
  /** Milliseconds past due (0 if not overdue) */
  overdueMs: number;
  isOverdue: boolean;
  overdueByLabel: string | null;
  dueInLabel: string | null;
  hasTimingData: boolean;
}

export type InactionState = 'FRESH' | 'DUE' | 'OVERDUE' | 'STALE';

export interface InactionResult {
  state: InactionState;
  /**
   * Absolute deadline for the current touch window, if we can compute it.
   */
  dueAt: Date | null;
  /**
   * Milliseconds until SLA deadline (positive) – only for FRESH / DUE.
   */
  dueInMs: number | null;
  /**
   * Milliseconds past SLA deadline (positive) – only for OVERDUE / STALE.
   */
  overdueByMs: number | null;
  /**
   * Human-readable labels for UI.
   */
  dueInLabel: string | null;
  overdueByLabel: string | null;
  /**
   * One-line DSOS-style explanation for "Why now".
   */
  whyNow: string;
  /**
   * 0–100 UI-only severity, used for badge intensity.
   * This MUST NOT be used to change core sorting / business logic.
   */
  severity: number;
  /**
   * True if we had at least minimal temporal data to compute a state.
   */
  hasTimingData: boolean;
}

interface InactionInput {
  /**
   * Raw lead record – kept generic to avoid hard-coding schema.
   * We only read a minimal subset of fields and treat everything as optional.
   */
  lead: Pick<
    Lead,
    | 'created_at'
    | 'last_interaction_at'
    | 'source'
  > & {
    // Optional extended fields (if present in schema / API)
    first_contacted_at?: string | null;
    last_activity_at?: string | null;
    last_contacted_at?: string | null;   // Last OUTBOUND contact (call/whatsapp/email)
    next_follow_up_at?: string | null;   // Real due date; when set, overrides SLA-based due
    temperature?: string | null;
    attempt_count?: number | null;
    source_type?: string | null;
    insertedAt?: string | null;
  };
  /**
   * Optional "now" override for testing.
   */
  now?: Date;
}

/** Parse ISO/Date to Date; UTC-safe. Returns null if invalid. */
function parseDate(value: string | Date | null | undefined): Date | null {
  if (!value) return null;
  const d = typeof value === 'string' ? new Date(value) : value;
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Compute lead timing: anchor = last contact or created_at; due = next_follow_up_at or anchor + SLA.
 * Time since contact is from created_at until first contact, then from last OUTBOUND contact.
 * Overdue = time past a real due date (next_follow_up_at or SLA-based).
 * UTC-safe; clamps future anchor to now; created_at fallback to insertedAt.
 */
export function computeLeadTiming(lead: LeadTimingInput, now: Date = new Date()): LeadTimingResult {
  const nowMs = now.getTime();

  const createdAt =
    parseDate(lead.created_at) ||
    parseDate((lead as any).insertedAt) ||
    null;
  const lastContactedAt =
    parseDate(lead.last_contacted_at) ||
    parseDate(lead.last_interaction_at) ||
    parseDate(lead.last_activity_at) ||
    parseDate(lead.first_contacted_at) ||
    null;

  const anchorAt = lastContactedAt || createdAt;
  if (!anchorAt) {
    return {
      anchorAt: null,
      timeSinceAnchorMs: 0,
      dueAt: null,
      overdueMs: 0,
      isOverdue: false,
      overdueByLabel: null,
      dueInLabel: null,
      hasTimingData: false,
    };
  }

  // Clamp anchor to now if in future (timezone bug)
  const clampedAnchorMs = Math.min(anchorAt.getTime(), nowMs);
  const anchorAtClamped = clampedAnchorMs < anchorAt.getTime() ? new Date(clampedAnchorMs) : anchorAt;
  const timeSinceAnchorMs = nowMs - anchorAtClamped.getTime();

  const nextFollowUpAt = parseDate(lead.next_follow_up_at);
  const slaMs = getSlaMs(lead);
  const dueAt = nextFollowUpAt
    ? nextFollowUpAt
    : new Date(anchorAtClamped.getTime() + slaMs);

  const overdueMs = Math.max(0, nowMs - dueAt.getTime());
  const isOverdue = overdueMs > 0;

  let overdueByLabel: string | null = null;
  let dueInLabel: string | null = null;
  if (isOverdue) {
    overdueByLabel = formatDuration(overdueMs);
  } else {
    const dueInMs = dueAt.getTime() - nowMs;
    if (dueInMs > 0) dueInLabel = formatDuration(dueInMs);
  }

  return {
    anchorAt: anchorAtClamped,
    timeSinceAnchorMs,
    dueAt,
    overdueMs,
    isOverdue,
    overdueByLabel,
    dueInLabel,
    hasTimingData: true,
  };
}

function getSourceCategory(lead: InactionInput['lead']): InactionSourceCategory {
  const raw =
    (lead as any).source_type ||
    lead.source ||
    '';

  const normalized = String(raw).toLowerCase();

  if (!normalized) return 'unknown';
  if (normalized.includes('inbound') || normalized.includes('demo') || normalized.includes('form')) {
    return 'inbound';
  }
  if (normalized.includes('referral') || normalized.includes('partner')) {
    return 'referral';
  }
  if (normalized.includes('outbound') || normalized.includes('cold') || normalized.includes('prospect')) {
    return 'outbound';
  }

  return 'unknown';
}

function formatDuration(ms: number): string {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const totalMinutes = Math.floor(totalSeconds / 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours <= 0) {
    if (minutes <= 0) return INACTION_COPY.duration.fewMinutes;
    return INACTION_COPY.duration.formatMinutes(minutes);
  }

  if (minutes === 0) return INACTION_COPY.duration.formatHours(hours);
  return INACTION_COPY.duration.formatHoursMinutes(hours, minutes);
}

function buildWhyNow(state: InactionState, neverContacted: boolean, labels: {
  dueInLabel?: string | null;
  overdueByLabel?: string | null;
  slaHours: number;
}): string {
  const { dueInLabel, overdueByLabel, slaHours } = labels;

  if (state === 'FRESH') {
    if (neverContacted) {
      return INACTION_COPY.whyNow.fresh.neverContacted(dueInLabel, slaHours);
    }
    return INACTION_COPY.whyNow.fresh.contacted;
  }

  if (state === 'DUE') {
    if (neverContacted) {
      return INACTION_COPY.whyNow.due.neverContacted;
    }
    return INACTION_COPY.whyNow.due.contacted;
  }

  if (state === 'OVERDUE') {
    if (neverContacted) {
      return INACTION_COPY.whyNow.overdue.neverContacted(overdueByLabel);
    }
    return INACTION_COPY.whyNow.overdue.contacted(overdueByLabel);
  }

  // STALE
  if (neverContacted) {
    return INACTION_COPY.whyNow.stale.neverContacted;
  }
  return INACTION_COPY.whyNow.stale.contacted;
}

/**
 * Core engine: compute inaction state from lead timestamps.
 *
 * - Anchor = last_contacted_at ?? last_interaction_at ?? created_at (time since contact).
 * - Due = next_follow_up_at ?? anchor + SLA(temperature). SLA: HOT 6h, WARM 24h, COLD 72h.
 * - Overdue = time past due date. UTC-safe; clamps future anchor to now.
 */
export function computeInaction(input: InactionInput): InactionResult {
  const now = input.now ?? new Date();
  const lead = input.lead;

  const timing = computeLeadTiming(lead as LeadTimingInput, now);
  if (!timing.hasTimingData || !timing.anchorAt || !timing.dueAt) {
    return {
      state: 'FRESH',
      dueAt: null,
      dueInMs: null,
      overdueByMs: null,
      dueInLabel: null,
      overdueByLabel: null,
      whyNow: INACTION_COPY.fallback.noTimingData,
      severity: 10,
      hasTimingData: false,
    };
  }

  const neverContacted = !(
    parseDate(lead.last_contacted_at) ||
    parseDate(lead.last_interaction_at) ||
    parseDate((lead as any).last_activity_at) ||
    parseDate((lead as any).first_contacted_at)
  );

  const slaMs = getSlaMs(lead);
  const slaHours = slaMs / (60 * 60 * 1000);
  const halfSlaMs = slaMs * (INACTION_THRESHOLDS.freshFraction ?? 0.5);
  const overdueWindowMs = INACTION_THRESHOLDS.overdueWindowHours * 60 * 60 * 1000;

  const timeToDueMs = timing.dueAt.getTime() - now.getTime();
  const timeOverdueMs = timing.overdueMs;

  let state: InactionState;
  if (timeToDueMs > halfSlaMs) {
    state = 'FRESH';
  } else if (timeToDueMs > 0) {
    state = 'DUE';
  } else if (timeOverdueMs < overdueWindowMs) {
    state = 'OVERDUE';
  } else {
    state = 'STALE';
  }

  let dueInMs: number | null = null;
  let overdueByMs: number | null = null;
  let dueInLabel: string | null = timing.dueInLabel;
  let overdueByLabel: string | null = timing.overdueByLabel;

  if (state === 'FRESH' || state === 'DUE') {
    if (timeToDueMs > 0) {
      dueInMs = timeToDueMs;
      if (!dueInLabel) dueInLabel = formatDuration(timeToDueMs);
    }
  } else {
    overdueByMs = timeOverdueMs;
    if (!overdueByLabel) overdueByLabel = formatDuration(timeOverdueMs);
  }

  let severity = INACTION_SEVERITY_BASE[state];
  if (state === 'FRESH' && dueInMs != null) {
    const progress = clamp(1 - timeToDueMs / slaMs, 0, 1);
    severity = clamp(severity + progress * 10, 0, 100);
  } else if (state === 'DUE' && dueInMs != null) {
    const progress = clamp(1 - timeToDueMs / slaMs, 0, 1);
    severity = clamp(severity + progress * 15, 0, 100);
  } else if (state === 'OVERDUE' && overdueByMs != null) {
    const progress = clamp(timeOverdueMs / overdueWindowMs, 0, 1);
    severity = clamp(severity + progress * 10, 0, 100);
  }

  const whyNow = buildWhyNow(state, neverContacted, {
    dueInLabel,
    overdueByLabel,
    slaHours,
  });

  return {
    state,
    dueAt: timing.dueAt,
    dueInMs,
    overdueByMs,
    dueInLabel,
    overdueByLabel,
    whyNow,
    severity,
    hasTimingData: true,
  };
}

